/**
 * Toast.jsx — Toast notification system (MUI Snackbar)
 */

import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Snackbar, Alert, Stack } from '@mui/material';

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const toast = useCallback((message, type = 'success') => {
    const id = ++toastId;
    setToasts((prev) => {
      const updated = prev.length >= 5 ? prev.slice(1) : prev;
      return [...updated, { id, message, type }];
    });
    timers.current[id] = setTimeout(() => removeToast(id), 3000);
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Stack spacing={1} sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 2000 }}>
        {toasts.map((t) => (
          <Snackbar key={t.id} open anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} sx={{ position: 'static' }}>
            <Alert
              severity={t.type === 'error' ? 'error' : t.type === 'info' ? 'info' : 'success'}
              variant="filled"
              onClose={() => removeToast(t.id)}
              sx={{ minWidth: 240 }}
            >
              {t.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
